import moment from 'moment'
import { toast } from './toast'
import { TimeOff, TimeOffDto } from '../types/TimeOff'
import { User } from '../types/User'

type UpdateTimeOffResult =
  | { unwrap: () => Promise<unknown> }
  | Promise<unknown>
  | unknown

interface SubmitTimeOffUpdateParams {
  selectedTimeoff: TimeOff
  allTimesOff: TimeOff[]
  timeOffOwner: User
  profile: User
  updateTimeOff: (args: {
    updatedTimeOff: TimeOffDto
    updatedBy: User
  }) => UpdateTimeOffResult
  onAfterSubmit?: () => void
}

async function awaitUpdateResult(result: UpdateTimeOffResult) {
  if (
    result &&
    typeof result === 'object' &&
    'unwrap' in result &&
    typeof result.unwrap === 'function'
  ) {
    await result.unwrap()
    return
  }

  if (result && typeof (result as Promise<unknown>).then === 'function') {
    await (result as Promise<unknown>)
  }
}

export async function submitTimeOffUpdate({
  selectedTimeoff,
  allTimesOff,
  timeOffOwner,
  profile,
  updateTimeOff,
  onAfterSubmit,
}: SubmitTimeOffUpdateParams): Promise<boolean> {
  if (
    !moment(selectedTimeoff.startDate, 'YYYY-MM-DD', true).isSameOrBefore(
      moment(selectedTimeoff.endDate, 'YYYY-MM-DD', true),
    )
  ) {
    toast.error('The date ranges are incorrect, please check them.', {
      toastId: 'create-timeoff-error',
    })
    return false
  }

  let isOverlapping = false
  const e1start = moment(selectedTimeoff.startDate)
  const e1end = moment(selectedTimeoff.endDate)
  allTimesOff.forEach(timeoff => {
    if (
      timeoff !== selectedTimeoff &&
      timeoff.userId === selectedTimeoff.userId &&
      timeoff.id !== selectedTimeoff.id
    ) {
      const e2start = moment(timeoff.startDate)
      const e2end = moment(timeoff.endDate)
      const overlaps =
        e1start.isSameOrBefore(e2end) && e1end.isSameOrAfter(e2start)
      if (overlaps) {
        toast.warning(
          `The modified Time Off is overlapping with another time off, ${timeoff.startDate}-${timeoff.endDate}`,
          {
            toastId: 'create-timeoff-warning',
          },
        )
        isOverlapping = true
      }
    }
  })

  if (isOverlapping) return false

  if (
    selectedTimeoff.googleCalendarEventId &&
    !timeOffOwner.hasGoogleCalendarConnected
  ) {
    toast.error(
      `${timeOffOwner.firstName} ${timeOffOwner.lastName} has not connected Google Calendar yet. The Time Off cannot be updated.`,
      {
        toastId: 'google-calendar-not-connected-update',
      },
    )
    return false
  }

  const updatedTimeOff: TimeOffDto = {
    ...selectedTimeoff,
    startDate: moment(selectedTimeoff.startDate).format('YYYY-MM-DD'),
    endDate: moment(selectedTimeoff.endDate).format('YYYY-MM-DD'),
  }

  try {
    await awaitUpdateResult(
      updateTimeOff({ updatedTimeOff, updatedBy: profile }),
    )
    onAfterSubmit?.()
    return true
  } catch {
    return false
  }
}
